import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { aegisApi, clearStoredTokens, getStoredToken, setStoredTokens } from 'api/aegis';
import { trackEvent } from 'utils/analytics';

const AUTH_REQUIRED_EVENT = 'aegis:auth-required';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => getStoredToken());
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));

  const loadCurrentUser = useCallback(async () => {
    if (!getStoredToken()) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setUser(await aegisApi.me());
    } catch {
      clearStoredTokens();
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  useEffect(() => {
    function handleAuthenticationRequired() {
      clearStoredTokens();
      setToken(null);
      setUser(null);
      setLoading(false);
    }

    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthenticationRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthenticationRequired);
  }, []);

  const login = useCallback(async (credentials, options = {}) => {
    const tokens = await aegisApi.login(credentials);
    setStoredTokens(tokens);
    setToken(tokens.access_token);
    setUser(await aegisApi.me());
    trackEvent('login', {
      method: 'password',
      redirect_target: options.redirectTarget || 'projects'
    });
  }, []);

  const register = useCallback(async (account) => {
    const tokens = await aegisApi.register(account);
    setStoredTokens(tokens);
    setToken(tokens.access_token);
    setUser(await aegisApi.me());
    trackEvent('sign_up', {
      method: 'password',
      organization_created: true
    });
  }, []);

  const acceptInvite = useCallback(async (invite) => {
    const tokens = await aegisApi.acceptInvite(invite);
    setStoredTokens(tokens);
    setToken(tokens.access_token);
    setUser(await aegisApi.me());
    trackEvent('sign_up', {
      method: 'invite',
      organization_created: false
    });
  }, []);

  const updateProfile = useCallback(async (profile) => {
    const updated = await aegisApi.updateMe(profile);
    setUser(updated);
    return updated;
  }, []);

  const changePassword = useCallback(async (passwords) => {
    const updated = await aegisApi.changePassword(passwords);
    setUser(updated);
    return updated;
  }, []);

  const logout = useCallback(() => {
    clearStoredTokens();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated: Boolean(token && user),
      acceptInvite,
      changePassword,
      loading,
      login,
      logout,
      register,
      refreshUser: loadCurrentUser,
      token,
      updateProfile,
      user
    }),
    [acceptInvite, changePassword, loadCurrentUser, loading, login, logout, register, token, updateProfile, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
